import React, {Component, createRef} from 'react'
import DatePicker from 'react-datepicker'
import moment from 'moment'

import {runByFeature} from './utils/runByFeature'
import {getDateAndTimeFormat, getModesAndSettings} from './utils/dateEndTime'
import {getMobileHeaderObserver, getMonthObserver, getYearsObserver} from './dateTimeMutations'
import {convertDatesForState, createDateObject, createDateObjectV14, createTimeObject, convertTimeByTimeZone, createTimeObjectV14 } from './utils/dateConvertions'
import {stringifyForDUCOutput, stringifyForDucOutput} from './utils/formatData'

import CustomInput from './uiComponents/CustomInput/CustomInput'
import TimePicker from './uiComponents/TimePicker'

import 'react-datepicker/dist/react-datepicker.css'

const ValidationStatus = {
  NotValidated: 0,
  Valid: 1,
  NotValid: 2
}

class DateTimePicker extends Component {
  constructor (props) {
    super(props)
    this.monthObserver = null
    this.yearsOserver = null
    this.mobileHeaderObserver = null

    this.dateValidationStatus = ValidationStatus.NotValidated

    this.dateTarget = ''
    this.settings = getModesAndSettings(props.options)
    this.formats = getDateAndTimeFormat()
    this.required = props.required
    this.features = this.props.features

    this.state = {
      dateIsOpen: false,
      dateForState: undefined,
      dateTime: {
        date: undefined,
        time: undefined
      }
    }
    this.timeRef = createRef()
    this.previousValue = this.props.value
  }

  static toAPIValue(value) {
    const mDateWithTimeZone = moment(value, 'YYYY-MM-DDTHH:mm:ssZ', true)
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

    if (mDateWithTimeZone.isValid()) {
      return {
        date: mDateWithTimeZone.format('YYYY-MM-DD'),
        time: mDateWithTimeZone.format('HH:mm:ss'),
        timeZone
      }
    }

    const mDate = moment(value, ['YYYY-MM-DDTHH:mm:ss', 'YYYY-MM-DD'], true)
    const mTime = moment(value, ['YYYY-MM-DDTHH:mm:ss', 'HH:mm:ss'], true)

    return {
      date: mDate.isValid() ? mDate.format('YYYY-MM-DD'): '',
      time: mTime.isValid() ? mTime.format('HH:mm:ss'): '',
      timeZone
    }
  }

  setDatePickerState = (value) => !this.props.readonly && this.setState({dateIsOpen: value})

  setTimePickerState = (value) => {
    if(value) {
      this.timeValidationStatus = ValidationStatus.NotValidated
    }
    this.setState({ timeIsOpen: value })
  }

  onDateChange = (value) =>  {
    const { dateTime : { time }} = this.state
    const newDateObject = createDateObject(value, time, this.formats, this.settings)
    runByFeature(this.features, {
      newTimezoneModel: () => runByFeature(this.features, {
        newStaticProduct: () => this.props.onChange(stringifyForDucOutput(newDateObject), [], this.settings.showDateTime && (!value || !time)),
        otherwise: () => this.props.onChange(stringifyForDUCOutput(newDateObject))
      }),
      otherwise: () => {
        const innerFormats = [this.formats.dateFormat, `${this.formats.dateFormat} ${this.formats.timeFormat}`]
        this.props.onChange(value && moment(value, innerFormats, true).format('YYYY-MM-DDTHH:mm:ss'))
      }
    })
  }

  componentDidMount () {
    const { value, options, t } = this.props
    const { showDate, showTime, showDateTime, screenSize } = this.settings
    if (this.timeRef && this.timeRef.current) {
      this.monthObserver = getMonthObserver(this.timeRef.current, options)
      this.monthObserver.observe(this.timeRef.current, { attributes: false, childList: true, subtree: true })

      this.yearsOserver = getYearsObserver(this.timeRef.current)
      this.yearsOserver.observe(this.timeRef.current, { attributes: true, childList: true, subtree: true })

      if (window.innerWidth <= Number(screenSize)) {
        this.mobileHeaderObserver = getMobileHeaderObserver(
          this.timeRef.current,
          t,
          screenSize,
          this.setDatePickerState,
          this.setTimePickerState
        )

        this.mobileHeaderObserver.observe(this.timeRef.current, {
          childList: true,
          attributes: false,
          subtree: true,
          characterData: false
        })
      }
    }

    if(value) {
      const { dateForState, date, time } = convertDatesForState(value, { showDateTime, showTime, showDate })
      runByFeature(this.features, {
        newTimezoneModel: () => {
          this.setState({
            dateForState,
            dateTime: {
              date,
              time,
            }
          })
        },
        otherwise: () => {
          this.setState({
            dateForState,
            dateTime: {
              date,
              time: time ? time : dateForState ? moment(dateForState).format('HH:mm') : '',
            }
          })
        }
      })
    }
  }

  componentDidUpdate(prevProps, prevState, prevContext) {
    let isShouldUpdate = runByFeature(this.features, {
      newTimezoneModel: () => runByFeature(this.features, {
        newStaticProduct: () => prevProps.value !== null
          && (moment(this.props.value).isValid())
          && !moment(this.props.value).isSame(moment(prevProps.value)
        ),
        otherwise: () => prevProps.value !== null && prevProps.value !== this.props.value
      }),
      otherwise: () => prevProps.value !== this.props.value
    })
    if (isShouldUpdate) {
      const { showDate, showTime } = this.settings
      const { timeFormat, dateFormat } = this.formats
      const timeWithoutTimeZone = convertTimeByTimeZone(this.props.value, prevState.dateTime, this.state.dateTime)
      const mDate =  moment(timeWithoutTimeZone, ['YYYY-MM-DDTHH:mm:ss', 'YYYY-MM-DDTHH:mm:ssZ',  'YYYY-MM-DD'], true)
      const mTime =  moment(timeWithoutTimeZone, ['YYYY-MM-DDTHH:mm:ss', 'YYYY-MM-DDTHH:mm:ssZ', 'HH:mm:ss'], true)

      if (mDate.isValid() || mTime.isValid()) {
        this.setState({
          ...this.state,
          dateForState: mDate.isValid() ? mDate.toDate() : '',
          dateTime: {
            date: showDate && mDate.isValid() ? mDate.format(dateFormat) : '',
            time: showTime && mTime.isValid() ? mTime.format(timeFormat) : '',
          }
        }, () => {
          let newDateFormat = `${showDate ? dateFormat : ''} ${showTime ? timeFormat : ''}`
          newDateFormat = showDate && !mTime.isValid() ? dateFormat : newDateFormat
          newDateFormat = showTime && !mDate.isValid() ? timeFormat : newDateFormat
          const newDate = mDate.isValid() ? mDate.format(newDateFormat) : mTime.isValid() ? mTime.format(newDateFormat) : ''

          if(mDate.isValid()) {
            this.onDateChange(newDate)
          } else if (mTime.isValid()) {
            this.handleTimeChange(newDate, true)
          }
        })
      }
    } else if (this.props.value === '' && prevProps.value !== '') {
      this.setState({
        dateForState: undefined,
        dateTime: {
          date: undefined,
          time: undefined,
        }
      })
    }
  }

  componentWillUnmount() {
    this.monthObserver.disconnect()
    this.yearsOserver.disconnect()

    if (this.mobileHeaderObserver) {
      this.mobileHeaderObserver.disconnect()
    }
  }

  // Date handle
  handleDateChange = (date) => {
    const { dateTime } = this.state
    let currentDate = date

    if (currentDate !== 'Invalid Date') {
      const current = moment(currentDate)

      let onlyDate = moment(current).format(this.formats.dateFormat)

      const { options } = this.props
      if (options?.custom?.datePickerParams) {
        const { dateMinValue, dateMaxValue } = options?.custom?.datePickerParams

        if (!!dateMinValue) {
          const minValue = moment(dateMinValue)
          if (current.isSameOrBefore(minValue)) {
            onlyDate = minValue.format(this.formats.dateFormat)
            currentDate = minValue.toDate()
          }
        }

        if (!!dateMaxValue) {
          const maxValue = moment(dateMaxValue)
          if (current.isAfter(maxValue)) {
            onlyDate = maxValue.format(this.formats.dateFormat)
            currentDate = maxValue.toDate()
          }
        }
      }

      if(currentDate){
        this.setState({
          dateForState: currentDate,
          dateTime: {
            ...dateTime,
            date: onlyDate
          }
        })

        runByFeature(this.features, {
          newTimezoneModel: () => {
            this.onDateChange(moment(currentDate).format(this.formats.dateFormat))
            this.dateValidationStatus = ValidationStatus.NotValidated
          },
          otherwise: () => {
            this.props.onChange(createDateObjectV14(currentDate, dateTime.time, this.formats, this.settings))
            this.dateValidationStatus = ValidationStatus.NotValidated
          }
        })

        if (!this.dateTarget) {
          this.setDatePickerState(false)
        }
      } else {
        this.setState({
          dateForState: '',
          dateTime: {
            ...dateTime,
            date: ''
          }
        })
        runByFeature(this.features, {
          newTimezoneModel: () => {
            const mDate = moment(currentDate)
            this.onDateChange(mDate.isValid() ? mDate.format(this.formats.dateFormat) : '')
            this.dateValidationStatus = ValidationStatus.NotValidated
            if (!this.dateTarget) {
              this.setDatePickerState(false)
            }
          }
        })
      }
    }
  }

  closestRoundTime = (str) => {
    const mDate = moment(str, this.formats.dateFormat, true)
    const remainder = 30 - (mDate.minute() % 30);
    return mDate.add(remainder,'minutes').toDate()
  }

  onChangeRawDate = (e) => {
    if (e.target.value) {
      this.dateTarget = e.target.value

      this.setDatePickerState(true)
      try {
        const { dateFormat } = this.formats
        this.dateValidationStatus = ValidationStatus.NotValid

        const mDate = moment(`${e.target.value}`, [dateFormat, dateFormat.split('').reverse().join('').toUpperCase()], true)
        if (mDate.isValid()) {
          const current = this.closestRoundTime(e.target.value)
          this.handleDateChange(current)
        }
      }
      catch (e) {
        console.warn('date parsing failed', e);
        this.dateValidationStatus = ValidationStatus.NotValid
      }
    } else {
      this.dateValidationStatus = ValidationStatus.NotValidated
      this.handleDateChange('')
    }
  }

  handleOnDateBlur = (e) => {
    const { onBlur, id } = this.props
    const { dateForState, dateTime: {date, time} } = this.state

    this.dateTarget = ''
    runByFeature(this.features, {
      newTimezoneModel: () => {
        if (this.required) {
          if (!time && this.settings.showDateTime) {
            return
          }
        }
        this.onChangeRawDate(e)
        const stringJSON =  runByFeature(this.features, {
          newStaticProduct: () => dateForState && stringifyForDucOutput(createDateObject(date, time, this.formats, this.settings)),
          otherwise: () => dateForState && stringifyForDUCOutput(createDateObject(date, time, this.formats, this.settings))
        })
        onBlur(id, stringJSON || undefined)
      },
      otherwise: () => onBlur(id, createDateObjectV14(dateForState, time, this.formats, this.settings))
    })

  };

  // Time handle
  handleTimeChange = (value, forceUpdate) => {
    const { dateTime } = this.state
    const { timeFormat } = this.formats

    runByFeature(this.features, {
      newTimezoneModel: () => {
        this.setState({
          dateTime: {
            ...dateTime,
            time: value
          }
        }, () => forceUpdate && this.handleOnTimeBlur())
      },
      otherwise: () => {
        const valueToSend = value ? moment(value, timeFormat, true).format('HH:mm') : ''
        this.setState({
          dateTime: {
            ...dateTime,
            time: valueToSend
          }
        })
        this.props.onChange(createTimeObjectV14(value, dateTime.date, this.formats, this.settings))
      }
    })
  }

  handleOnTimeBlur = () => {
    const { onBlur, id } = this.props
    const { dateTime: { date, time } } = this.state

    runByFeature(this.features, {
      newTimezoneModel: () => runByFeature(this.features, {
        newStaticProduct: () => {
          const dateObject = createTimeObject(time, date, this.formats, this.settings)
          this.props.onChange(stringifyForDucOutput(dateObject), [], this.settings.showDateTime && (!date || !time))
        },
        otherwise: () => {
          if (this.required) {
            if (!date && this.settings.showDateTime) {
              return
            }
          }
          const dateObject = createTimeObject(time, date, this.formats, this.settings)
          this.props.onChange(stringifyForDUCOutput(dateObject))
        }
      }),
      otherwise: () => {
        onBlur(id, createTimeObjectV14(time, date, this.formats, this.settings))
      }
    })
  };

  render () {
    const {
      readonly,
      t,
    } = this.props

    const { dateIsOpen, dateForState } = this.state
    const { langCode, timeFormat, dateFormat } = this.formats
    const { showDate, showTime, showDateTime, minDate, maxDate, yearsLength } = this.settings
    const dateFormatForDatePicker = dateFormat.replaceAll('Y', 'y').replaceAll('D', 'd')
    return (
      <div className='date-time-duc' ref={this.timeRef}>
        <div className={`form-control date-picker datetime ${!showDateTime ? 'fullwidth' : ''}`}>
          { showDate &&
          <DatePicker
            onSelect={this.setDatePickerState.bind(this, false)}
            open={dateIsOpen}
            selected={dateForState}
            disabled={readonly}
            onChange={(date) => {
              if(this.dateValidationStatus !== ValidationStatus.NotValid) {
                this.dateValidationStatus = ValidationStatus.NotValidated
                if (!readonly) {
                  this.handleDateChange(date)
                }
              }
            }}
            onBlur={this.handleOnDateBlur}
            inline={false}
            minDate={minDate}
            maxDate={maxDate}
            closeOnScroll={false}
            disablePast
            onChangeRaw={(e) => {
              if(e.target.value !== undefined) {
                this.onChangeRawDate(e)
              }
            }}
            dateFormat={dateFormatForDatePicker}
            showMonthDropdown
            scrollableYearDropdown
            yearDropdownItemNumber={yearsLength}
            showYearDropdown
            locale={langCode}
            customInput={
              <CustomInput onInputClick={this.setDatePickerState.bind(this, true)}
                icon="calendar.svg"
                placeholderText={t('ducs.set_date')}
                ref={this.ref}
              />
            }
            showPopperArrow={false}
            popperPlacement="bottom-start"
            onClickOutside={this.setDatePickerState.bind(this, false)}
          />
          }
          {showTime &&
          <TimePicker
            format={timeFormat}
            timeInterval={30}
            value={this.props.value}
            stateValue={this.state.dateTime.time}
            onChange={this.handleTimeChange}
            onBlur={this.handleOnTimeBlur}
            disabled={readonly}
            disablePast
            t={t}
            features={this.features}
          />
          }
        </div>
      </div>
    )
  }
}

if (typeof window !== 'undefined') {
  window.uStoreDucs = window.uStoreDucs || []
  window.uStoreDucs.push({ name: 'dateTimePicker', component: DateTimePicker, displayName: 'DateTime Picker' })
}
export default DateTimePicker
